import React, { useEffect } from 'react';
import { Box, CardContent, Alert, Snackbar, CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useGetWorkflowQuery, useUpdateWorkflowMutation } from '../../../services/WorkflowService';
import { clearWorkflow, markAsSaved } from '../state';
import { toUpdateRequest } from '../../../services/WorkflowService';
import { WorkflowDetailsHeader, BasicInfoSection, ConditionsSection, StepsSection, UnsavedChangesDialog } from '../components';
import { PageContainer, PageCard } from '../../common/components';
import { WorkflowValidationProvider } from '../components/shared';
import { useWorkflowForm } from '../hooks/useWorkflowForm';

const SUCCESS_MESSAGE = 'Workflow updated successfully!';

/** Inner form wrapped by WorkflowValidationProvider */
const WorkflowDetailForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const {
    dispatch, currentWorkflow,
    showSuccess, setShowSuccess,
    errorMessage, clearError,
    validateAndSubmit,
    showDialog, confirmLeave, cancelLeave, guardedNavigate,
  } = useWorkflowForm();

  const [updateWorkflow] = useUpdateWorkflowMutation();
  
  const { 
    isLoading: isFetching, 
    isError,
    error 
  } = useGetWorkflowQuery(id || '', {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (id) dispatch(clearWorkflow());
  }, [dispatch, id]);

  useEffect(() => {
    return () => { dispatch(clearWorkflow()); };
  }, [dispatch]);

  const handleSave = () => validateAndSubmit(async () => {
    await updateWorkflow(toUpdateRequest(currentWorkflow!, id!)).unwrap();
    dispatch(markAsSaved());
  }, 'Failed to update workflow. Please try again.');

  if (isFetching) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Box textAlign="center">
            <CircularProgress size={40} sx={{ color: '#3b82f6' }} />
            <Box mt={2} color="#64748b">Loading workflow details...</Box>
          </Box>
        </Box>
      </PageContainer>
    );
  }

  if (isError || !id) {
    const errMsg = !id
      ? 'No workflow ID provided. Please provide a valid workflow ID in the URL.'
      : 'Failed to load workflow. Please check the workflow ID and try again.';

    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" mt={4}>
          <Alert severity="error" sx={{ maxWidth: 600, '& .MuiAlert-message': { fontSize: '1rem' } }}>
            {errMsg}
            {isError && error && 'message' in error && (
              <Box mt={1} color="#b91c1c" fontSize="0.875rem">Details: {error.message}</Box>
            )}
          </Alert>
        </Box>
      </PageContainer>
    );
  }

  if (!currentWorkflow) return null;

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <WorkflowDetailsHeader 
            onSave={handleSave}
            onCancel={() => guardedNavigate('/workflows')}
            workflowName={currentWorkflow.name}
          />

          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
                {errorMessage}
              </Alert>
            )}

            <BasicInfoSection />
            <ConditionsSection />
            <StepsSection />
          </CardContent>
        </PageCard>
      </Box>

      <Snackbar
        open={showSuccess}
        autoHideDuration={1500}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>{SUCCESS_MESSAGE}</Alert>
      </Snackbar>

      <UnsavedChangesDialog open={showDialog} onConfirm={confirmLeave} onCancel={cancelLeave} />
    </PageContainer>
  );
};

const WorkflowDetailPage: React.FC = () => (
  <WorkflowValidationProvider>
    <WorkflowDetailForm />
  </WorkflowValidationProvider>
);

export default WorkflowDetailPage;
